<script setup>
import { computed, reactive } from 'vue';
import Projects from "./Projects.vue";
import Card from "./Card.vue";
import Storylets from "./Storylets.vue";
import Variables from "./Variables.vue";
import Languages from "./Languages.vue";
import Settings from "./Settings.vue";
import Preview from "./Preview.vue";
import FilterSelect from "./FilterSelect.vue";
import AppIcon from "./AppIcon.vue";

import { cardTitleBg } from "../utils/ui.js";
import {
  storyletsWindow,
  languagesWindow,
  projectsWindow,
  variablesWindow,
  noWindow,
  helpWindow,
  previewWindow,
  settingsWindow,
} from "../utils/windows.js";

const publicPath = process.env.BASE_URL ?? './';

const props = defineProps({
  storyData: Object,
  edit: Object,
  session: Object,
  apiUrl: String,
  backend: Object,
  uploadedData: Object,
  importActions: Array,
  dragging: String,
  cardClipboard: { type: Object, default: null },
});

const deleteConfirm = reactive({ waiting: false });

function handleDeleteSelected() {
  if (deleteConfirm.waiting) {
    emit('delete-selected');
    deleteConfirm.waiting = false;
  } else {
    deleteConfirm.waiting = true;
  }
}

const storyletOptions = computed(() => {
  if (!props.storyData?.stories) return [];
  return Object.entries(props.storyData.stories).map(([key, story]) => ({
    value: key,
    label: props.storyData.loc[story.info.name]?.en ?? key,
  }));
});

const emit = defineEmits([
  "set-story",
  "close-win",
  "download-data",
  "upload-data",
  "cancel-import",
  "save-data",
  "set-current-story",
  "delete-language",
  "add-language",
  "set-language",
  "over",
  "card-down",
  "card-up",
  "edit-card",
  "delete-card",
  "edit-previous-card",
  "drop-target",
  "node-out-down",
  "link-add",
  "debug-info",
  "scale-view",
  "dragging-card",
  "lines-refresh",
  "redraw-lines",
  "delete-selected",
  "cut-selected",
  "copy-selected",
  "paste-cards",
]);
</script>

<template>
  <div class="row" id="main-layout">
    <div class="column" id="main-editor">
      <div id="shelf">
        <div v-if="session.user" class="shelf-button" title="Projects" :class="edit.projectsWindow ? 'selected' : ''" @click="
          projectsWindow(edit);
        $refs.projWin.fetchProjects();
        ">
          <AppIcon name="floppy-disk" />
        </div>
        <div v-if="storyData.stories" class="shelf-button" title="Storylets" :class="edit.storyletsWindow ? 'selected' : ''"
          @click="storyletsWindow(edit)">
          <AppIcon name="book" />
        </div>
        <div v-if="storyData.stories" class="shelf-button" title="Variables" :class="edit.variablesWindow ? 'selected' : ''"
          @click="variablesWindow(edit)">
          <AppIcon name="wand-magic-sparkles" />
        </div>
        <div v-if="storyData.stories" class="shelf-button" title="Languages" :class="edit.languagesWindow ? 'selected' : ''"
          @click="languagesWindow(edit)">
          <AppIcon name="globe" />
        </div>
        <div v-if="storyData.stories" class="shelf-button" title="Settings" :class="edit.settingsWindow ? 'selected' : ''"
          @click="settingsWindow(edit)">
          <AppIcon name="gear" />
        </div>
        <div v-if="storyData.stories" class="shelf-button" title="Preview" @click="previewWindow(edit)"
          :class="edit.previewWindow ? 'selected' : ''">
          <AppIcon name="eye" />
        </div>
        <div v-if="session.user" class="shelf-button" title="Help" @click="helpWindow(edit)"
          :class="edit.helpWindow ? 'selected' : ''">
          <AppIcon name="question" />
        </div>
        <div class="saving" v-if="edit.saving"></div>
      </div>
      <div class="shelf" :class="edit.projectsWindow ? 'show' : ''">
        <Projects :apiUrl="apiUrl" :backend="backend" ref="projWin" :edit="edit" :data="storyData"
          :uploadedData="uploadedData" :importActions="importActions" @set-story="$emit('set-story', $event)"
          @close-win="noWindow(edit)" @download-data="$emit('download-data')" @upload-data="$emit('upload-data')"
          @cancel-import="$emit('cancel-import')" @save-data="$emit('save-data')" :session="session" />
      </div>
      <div class="shelf" :class="edit.storyletsWindow ? 'show' : ''">
        <Storylets :data="storyData" :edit="edit" v-if="edit.storyletsWindow"
          @set-story="$emit('set-current-story', $event)" @close-win="noWindow(edit)" />
      </div>
      <div class="shelf" :class="edit.variablesWindow ? 'show' : ''">
        <Variables :data="storyData.variableDefs" v-if="edit.variablesWindow" @close-win="noWindow(edit)" />
      </div>
      <div class="shelf" :class="edit.languagesWindow ? 'show' : ''">
        <Languages :data="storyData" :edit="edit" @set-lang="$emit('set-language', $event)"
          @delete-lang="$emit('delete-language', $event)" @add-lang="$emit('add-language')" v-if="edit.languagesWindow"
          @close-win="noWindow(edit)" />
      </div>
      <div class="shelf" :class="edit.settingsWindow ? 'show' : ''">
        <Settings v-if="edit.settingsWindow" @close-win="noWindow(edit)" :storyData="storyData" :edit="edit" />
      </div>
      <div class="shelf" :class="edit.previewWindow ? 'show' : ''">
        <Preview ref="prevWin" :data="storyData" :edit="edit" v-if="edit.previewWindow" :apiUrl="apiUrl"
          :user="session.user" />
      </div>
      <div class="shelf line"></div>

      <div id="main">
        <div id="cards-toolbar" class="button-bar row" v-if="session.user" @mouseover="$emit('over', 'toolbar')">
          <div class="selection-toolbar" v-if="edit.selectedCards.length > 0 || cardClipboard">
            <template v-if="edit.selectedCards.length > 0">
              <span class="selection-count">
                {{ edit.selectedCards.length }} Card{{ edit.selectedCards.length !== 1 ? 's' : '' }}:
              </span>
              <button
                :class="['ui-button', deleteConfirm.waiting ? 'ui-button-red' : 'ui-button-plain']"
                :title="deleteConfirm.waiting ? 'Confirm delete' : 'Delete selected'"
                @click="handleDeleteSelected"
                @mouseleave="deleteConfirm.waiting = false">
                <AppIcon :name="deleteConfirm.waiting ? 'question' : 'trash'" />
              </button>
              <button class="ui-button ui-button-plain" title="Cut selected" @click="$emit('cut-selected')">
                <AppIcon name="scissors" />
              </button>
              <button class="ui-button ui-button-plain" title="Copy selected" @click="$emit('copy-selected')">
                <AppIcon name="copy" />
              </button>
            </template>
            <button v-if="cardClipboard" class="ui-button ui-button-plain" title="Paste" @click="$emit('paste-cards')">
              <AppIcon name="paste" />
            </button>
          </div>
          <div style="flex-grow: 1; text-align: center">
            <div class="button-container hide" v-if="storyData.stories">
              <button @click="$emit('debug-info')">Debug</button>
            </div>
            <div class="button-container left-pad" v-if="storyData.stories">
              <button v-if="1 == 2" :style="{
                background: cardTitleBg('card'),
                color: 'black',
                cursor: 'grab',
              }">
                #
              </button>
              <button v-if="1 == 2" @click="$emit('scale-view', -0.1)" :style="{
                background: cardTitleBg('card'),
                color: 'black',
              }">
                -
              </button>
              <button v-if="1 == 2" @click="$emit('scale-view', 0.1)" :style="{
                background: cardTitleBg('card'),
                color: 'black',
              }">
                +
              </button>
              <span v-if="1 == 2">{{
                Math.floor(100 * edit.cardsScale)
              }}</span>
              <button @mousedown="$emit('dragging-card', 'card')" :style="{
                background: cardTitleBg('card'),
                color: '#eee',
                cursor: 'grab',
              }">
                Content
              </button>
            </div>
            <div class="button-container" v-if="storyData.stories">
              <button @mousedown="$emit('dragging-card', 'logic')" :style="{
                background: cardTitleBg('logic'),
                color: '#eee',
                cursor: 'grab',
              }">
                Logic
              </button>
            </div>
            <div class="button-container" v-if="storyData.stories">
              <button @mousedown="$emit('dragging-card', 'operator')" :style="{
                background: cardTitleBg('operator'),
                color: '#eee',
                cursor: 'grab',
              }">
                Operator
              </button>
            </div>
            <div class="button-container" v-if="storyData.stories">
              <button @mousedown="$emit('dragging-card', 'detour')" :style="{
                background: cardTitleBg('detour'),
                color: '#eee',
                cursor: 'grab',
              }">
                Storylet
              </button>
            </div>
            <div class="button-container" v-if="storyData.stories">
              <button @mousedown="$emit('dragging-card', 'meta')" :style="{
                background: cardTitleBg('meta'),
                color: '#eee',
                cursor: 'grab',
              }">
                Meta
              </button>
            </div>
            <div class="button-container" v-if="storyData.stories">
              <button @mousedown="$emit('dragging-card', 'note')" :style="{
                background: cardTitleBg('note'),
                color: '#eee',
                cursor: 'grab',
              }">
                Note
              </button>
            </div>
          </div>
          <div style="flex-shrink: 1">
            <div class="button-container zoom-control" v-if="storyData.stories">
              <button class="ui-button ui-button-plain" @click="$emit('scale-view', -0.1)" title="Zoom out">
                <AppIcon name="minus" />
              </button>
              <span class="zoom-level">{{ Math.round(edit.cardsScale * 100) }}%</span>
              <button class="ui-button ui-button-plain" @click="$emit('scale-view', 0.1)" title="Zoom in">
                <AppIcon name="plus" />
              </button>
            </div>
            <div class="button-container" v-if="storyData.stories">
              <FilterSelect
                v-model="edit.currentStorylet"
                :options="storyletOptions"
                size="small"
                color="green"
                @change="$emit('lines-refresh')"
              />
            </div>
            <div class="button-container" v-if="storyData.stories">
              <select v-model="storyData.currentLanguage" class="ui-select ui-button-blue ui-select-small">
                <option v-bind:key="langindex" v-for="(lang, langindex) in storyData.loc.na" :value="langindex">
                  {{ storyData.loc.na[langindex] }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div v-if="!storyData.stories" style="
          position: absolute; inset: 0; margin-left: 64px;
          display: flex; align-items: center; justify-content: center;
          z-index: 50; pointer-events: none;">
          <img :src="`${publicPath}images/tvain-logo.svg`" style="width: 380px; opacity: 0.6;" />
        </div>

        <div id="cards" v-if="storyData.stories" @mouseover="$emit('over', 'cards')"
          :style="storyData.settings?.displayGrid === false ? { backgroundImage: 'none' } : {}">
          <div id="scaler-outer">
          <div id="scaler">
            <div id="snapGuide" :style="{
              display: dragging != 'none' ? 'block' : 'none',
              pointerEvents: 'none',
            }"></div>
            <div v-if="edit.selectionBox?.active" :style="{
              position: 'absolute',
              left: edit.selectionBox.x + 'px',
              top: edit.selectionBox.y + 'px',
              width: edit.selectionBox.w + 'px',
              height: edit.selectionBox.h + 'px',
              border: '2px dashed #24C3D5',
              backgroundColor: 'rgba(36, 195, 213, 0.1)',
              pointerEvents: 'none',
              zIndex: 9999,
            }"></div>
            <div id="linesSVG" style="width: 100%; height: 100%">
              <svg></svg>
            </div>

            <Card v-for="(data, key) in storyData.stories[
              edit.currentStorylet
            ].cards" @title-down="$emit('card-down', $event)" @title-up="$emit('card-up')"
              @edit-card="$emit('edit-card', $event)" @delete-card="$emit('delete-card', $event)"
              @edit-previous-card="$emit('edit-previous-card')" @drop-target="$emit('drop-target', $event)"
              @node-out-down="$emit('node-out-down', $event)" @link-add="$emit('link-add', $event)"
              @set-story="$emit('set-current-story', $event)" v-bind:key="key" :cardId="key" :cardData="data"
              :editData="edit" :storyData="storyData" :currentCardEditing="edit.editingCard"
              @redraw-lines="$emit('lines-refresh')" :selectedCards="edit.selectedCards">
            </Card>
          </div>
          </div><!-- scaler-outer -->
        </div>
      </div>

      <div id="connectlineSVG" style="width: 100%; height: 100%">
        <svg></svg>
      </div>

    </div>

    <div id="cardAddSwatch" :style="{
      display: edit.draggingCardAdd != 'none' ? 'block' : 'none',
      backgroundColor: cardTitleBg(edit.draggingCardAdd),
      color: '#eee',
      cursor: 'grab',
      transform: `scale(${edit.cardsScale})`,
      transformOrigin: '0 0',
    }"></div>
  </div>
</template>
